import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  Search,
  Copy,
  TrendingUp,
  Lock,
  Globe,
  MessageSquare,
  EyeOff,
  Loader2,
  Zap,
  LayoutGrid,
  List,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../services/api';
import { PermissionGuard } from '../components/Permissions/PermissionGuard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardFooter,
} from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

interface CannedResponse {
  id: number;
  short_code: string;
  content: string;
  is_private: boolean;
  usage_count: number;
  user_id: number;
  created_at: string;
}

const CannedResponsesPage: React.FC = () => {
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [includePrivate, setIncludePrivate] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingResponse, setEditingResponse] = useState<CannedResponse | null>(null);
  const [responseToDelete, setResponseToDelete] = useState<CannedResponse | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [formData, setFormData] = useState({
    short_code: '',
    content: '',
    is_private: false,
  });

  useEffect(() => {
    fetchCannedResponses();
  }, [searchTerm, includePrivate]);

  const fetchCannedResponses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('include_private', includePrivate.toString());
      const response = await api.get(`/canned-responses/?${params}`);
      setCannedResponses(response.data);
    } catch (error: any) {
      toast({ title: 'Error', description: 'Failed to fetch canned responses', variant: 'destructive' });
      console.error('Error fetching canned responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ short_code: '', content: '', is_private: false });
    setEditingResponse(null);
  };

  const handleOpenDialog = (response?: CannedResponse) => {
    if (response) {
      setEditingResponse(response);
      setFormData({ short_code: response.short_code, content: response.content, is_private: response.is_private });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.short_code.trim() || !formData.content.trim()) {
      toast({ title: 'Validation error', description: 'Short code and content are required', variant: 'destructive' });
      return;
    }
    try {
      if (editingResponse) {
        await api.put(`/canned-responses/${editingResponse.id}`, formData);
        toast({ title: 'Response updated', description: 'Canned response updated successfully' });
      } else {
        await api.post('/canned-responses/', formData);
        toast({ title: 'Response created', description: 'Canned response created successfully' });
      }
      handleCloseDialog();
      fetchCannedResponses();
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save canned response', variant: 'destructive' });
    }
  };

  const handleDelete = async (response: CannedResponse) => {
    try {
      await api.delete(`/canned-responses/${response.id}`);
      toast({ title: 'Response deleted', description: 'Canned response deleted successfully' });
      fetchCannedResponses();
    } catch (error: any) {
      toast({ title: 'Error', description: 'Failed to delete canned response', variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setResponseToDelete(null);
    }
  };

  const handleUse = async (response: CannedResponse) => {
    try {
      await api.post(`/canned-responses/${response.id}/use`);
      await navigator.clipboard.writeText(response.content);
      toast({ title: 'Copied', description: 'Content copied to clipboard and usage tracked' });
      setCannedResponses((prev) =>
        prev.map((r) => (r.id === response.id ? { ...r, usage_count: r.usage_count + 1 } : r))
      );
    } catch {
      try {
        await navigator.clipboard.writeText(response.content);
        toast({ title: 'Copied', description: 'Content copied to clipboard' });
      } catch {
        toast({ title: 'Error', description: 'Failed to copy to clipboard', variant: 'destructive' });
      }
    }
  };

  const filteredResponses = cannedResponses.filter(
    (r) =>
      r.short_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatShortCode = (shortCode: string) =>
    shortCode.startsWith('!') ? shortCode : `!${shortCode}`;

  const maxUsage = Math.max(...cannedResponses.map((r) => r.usage_count), 0);
  const isHot = (r: CannedResponse) => r.usage_count >= 5 && r.usage_count >= maxUsage * 0.5;

  const stats = {
    total: cannedResponses.length,
    privateCount: cannedResponses.filter((r) => r.is_private).length,
    publicCount: cannedResponses.filter((r) => !r.is_private).length,
    totalUsage: cannedResponses.reduce((sum, r) => sum + r.usage_count, 0),
  };

  return (
    <div className="flex flex-1 flex-col gap-6 py-4 px-4 md:py-6 lg:px-6">

      {/* ── Header ───────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 shrink-0">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Canned Responses</h1>
            <p className="text-sm text-muted-foreground">
              Reusable message templates for faster support
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={fetchCannedResponses}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <PermissionGuard permission="canned_responses:create">
            <Button onClick={() => handleOpenDialog()} size="sm">
              <Plus className="h-4 w-4 mr-1.5" />
              New Response
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* ── KPI Strip ────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Responses</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.total}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-emerald-500/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Public</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.publicCount}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                <Globe className="h-5 w-5 text-emerald-500" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-amber-500/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Private</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.privateCount}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                <Lock className="h-5 w-5 text-amber-500" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-violet-500/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Uses</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.totalUsage}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/10">
                <TrendingUp className="h-5 w-5 text-violet-500" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
      </div>

      {/* ── Toolbar ──────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by short code or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <Switch
              id="include-private"
              checked={includePrivate}
              onCheckedChange={setIncludePrivate}
            />
            <Label htmlFor="include-private" className="cursor-pointer text-sm">
              Include private
            </Label>
          </div>
          <Separator orientation="vertical" className="h-5" />
          {/* View mode toggle */}
          <div className="flex items-center rounded-md border border-border overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'flex h-8 w-8 items-center justify-center transition-colors',
                viewMode === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              )}
              title="List view"
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'flex h-8 w-8 items-center justify-center transition-colors',
                viewMode === 'grid'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              )}
              title="Grid view"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredResponses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold">
              {searchTerm ? 'No matching responses' : 'No canned responses yet'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 mb-5">
              {searchTerm
                ? 'Try different keywords or clear the search'
                : 'Create reusable templates to speed up your support workflow'}
            </p>
            {!searchTerm && (
              <PermissionGuard permission="canned_responses:create">
                <Button onClick={() => handleOpenDialog()} size="sm">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Create First Response
                </Button>
              </PermissionGuard>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (

        /* ── List View ─────────────────────────────────── */
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filteredResponses.map((response) => (
                <div
                  key={response.id}
                  className="group flex items-center gap-4 px-4 py-3 hover:bg-accent/40 transition-colors"
                >
                  {/* Short code badge */}
                  <Badge
                    variant="outline"
                    className={cn(
                      'font-mono text-xs shrink-0 min-w-[90px] justify-center',
                      response.is_private
                        ? 'border-amber-400/60 text-amber-600 dark:text-amber-400'
                        : 'border-emerald-400/60 text-emerald-600 dark:text-emerald-400'
                    )}
                  >
                    {formatShortCode(response.short_code)}
                  </Badge>

                  {/* Content preview */}
                  <p className="flex-1 text-sm text-muted-foreground truncate min-w-0">
                    {response.content}
                  </p>

                  {/* Metadata badges */}
                  <div className="flex items-center gap-2 shrink-0">
                    {isHot(response) && (
                      <Badge
                        variant="outline"
                        className="gap-1 text-[10px] border-orange-400/60 text-orange-500 px-1.5 py-0.5"
                      >
                        <Zap className="h-2.5 w-2.5" /> Hot
                      </Badge>
                    )}
                    {response.is_private ? (
                      <Badge
                        variant="outline"
                        className="gap-1 text-[10px] border-amber-400/60 text-amber-500 px-1.5 py-0.5"
                      >
                        <Lock className="h-2.5 w-2.5" /> Private
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="gap-1 text-[10px] border-emerald-400/60 text-emerald-500 px-1.5 py-0.5"
                      >
                        <Globe className="h-2.5 w-2.5" /> Public
                      </Badge>
                    )}
                    <span className="flex items-center gap-1 text-xs text-muted-foreground w-14 justify-end">
                      <TrendingUp className="h-3 w-3" />
                      {response.usage_count}×
                    </span>
                  </div>

                  {/* Hover actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleUse(response)}
                      title="Copy & Use"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleOpenDialog(response)}
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => { setResponseToDelete(response); setDeleteConfirmOpen(true); }}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      ) : (

        /* ── Grid View ─────────────────────────────────── */
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredResponses.map((response) => (
            <Card
              key={response.id}
              className="flex flex-col overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Status accent bar */}
              <div
                className={cn(
                  'h-1 w-full shrink-0',
                  response.is_private ? 'bg-amber-400' : 'bg-emerald-400'
                )}
              />
              <CardContent className="flex-1 pt-4">
                {/* Header row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={cn(
                        'font-mono text-xs',
                        response.is_private
                          ? 'border-amber-400/60 text-amber-600 dark:text-amber-400'
                          : 'border-emerald-400/60 text-emerald-600 dark:text-emerald-400'
                      )}
                    >
                      {formatShortCode(response.short_code)}
                    </Badge>
                    {isHot(response) && (
                      <Badge
                        variant="outline"
                        className="gap-1 text-[10px] border-orange-400/60 text-orange-500 px-1.5"
                      >
                        <Zap className="h-2.5 w-2.5" /> Hot
                      </Badge>
                    )}
                    {response.is_private && (
                      <span title="Private">
                        <EyeOff className="h-3.5 w-3.5 text-amber-500" />
                      </span>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenDialog(response)}>
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleUse(response)}>
                        <Copy className="h-4 w-4 mr-2" /> Copy & Use
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => { setResponseToDelete(response); setDeleteConfirmOpen(true); }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <p className="text-sm text-muted-foreground line-clamp-3 min-h-[60px]">
                  {response.content}
                </p>
              </CardContent>

              <CardFooter className="flex items-center justify-between px-4 pb-4 pt-0">
                <Separator className="mb-3" />
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <TrendingUp className="h-3.5 w-3.5" />
                    <span>Used {response.usage_count}×</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => handleUse(response)}
                  >
                    <Copy className="h-3 w-3 mr-1" /> Use
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Mobile FAB */}
      <div className="fixed bottom-6 right-6 sm:hidden">
        <PermissionGuard permission="canned_responses:create">
          <Button
            size="icon"
            className="h-14 w-14 rounded-full shadow-lg"
            onClick={() => handleOpenDialog()}
          >
            <Plus className="h-6 w-6" />
          </Button>
        </PermissionGuard>
      </div>

      {/* ── Create / Edit Dialog ──────────────────────────── */}
      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>
              {editingResponse ? 'Edit Canned Response' : 'New Canned Response'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Short code */}
            <div className="space-y-1.5">
              <Label
                htmlFor="short-code"
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Short Code *
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground font-mono bg-muted px-2 py-1.5 rounded-md border border-input">
                  !
                </span>
                <Input
                  id="short-code"
                  value={formData.short_code}
                  onChange={(e) => setFormData({ ...formData, short_code: e.target.value })}
                  placeholder="welcome, thanks, pricing..."
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Agents type{' '}
                <span className="font-mono text-foreground">
                  !{formData.short_code || 'shortcode'}
                </span>{' '}
                to insert this response
              </p>
            </div>

            {/* Content */}
            <div className="space-y-1.5">
              <Label
                htmlFor="content"
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Response Content *
              </Label>
              <textarea
                id="content"
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                rows={5}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your response message here..."
              />
              <p className="text-xs text-muted-foreground text-right">
                {formData.content.length} characters
              </p>
            </div>

            {/* Live preview */}
            {formData.content && (
              <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Preview
                </p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{formData.content}</p>
              </div>
            )}

            {/* Privacy toggle row */}
            <div className="flex items-center justify-between rounded-lg border bg-muted/20 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Private response</p>
                <p className="text-xs text-muted-foreground">
                  Only visible to you — not shared with the team
                </p>
              </div>
              <Switch
                id="is-private"
                checked={formData.is_private}
                onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.short_code.trim() || !formData.content.trim()}
            >
              {editingResponse ? 'Update' : 'Create'} Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ───────────────────────────── */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Canned Response</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-mono font-semibold">
                !{responseToDelete?.short_code}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => { setDeleteConfirmOpen(false); setResponseToDelete(null); }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => responseToDelete && handleDelete(responseToDelete)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CannedResponsesPage;
